import React, { useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'clsx';
import { FloatLabel } from '../float-label';
import style from './float-text-area.module.less';
import { noop } from 'ui-components/default-props/noop';

export const FloatTextArea = React.forwardRef(
  (
    {
      label,
      placeholder = '',
      className = '',
      value = '',
      maxLength,
      hasError = false,
      disabled = false,
      onChange,
      onKeyDown = noop,
      onBlur: onBlurProp = noop,
      onFocus: onFocusProp = noop,
      id,
    },
    ref,
  ) => {
    const [slideLabelUp, setSlideLabelUp] = useState(!!(value && value.length));
    const input = useRef();
    const actualRef = ref || input;

    const elementClassName = classNames(
      style['float-textarea'],
      { [style.error]: hasError },
      { [style.disabled]: disabled },
      { [style.label]: label },
      { [style.noLabel]: !label },
      className,
    );

    const onClick = useCallback(() => {
      setSlideLabelUp(true);
      actualRef.current.focus();
    }, [setSlideLabelUp, actualRef]);

    const onFocus = useCallback(() => {
      setSlideLabelUp(true);
      onFocusProp();
    }, [onFocusProp]);

    const onBlur = useCallback(
      (e) => {
        setSlideLabelUp(!!e.target.value);
        onBlurProp();
      },
      [onBlurProp],
    );

    return (
      <div className={elementClassName} data-testid='float-text-area' onClick={onClick}>
        {label && <FloatLabel hasError={hasError} inputId={id} labelText={label} slideUp={slideLabelUp || disabled} />}
        <textarea
          ref={actualRef}
          data-testid='float-text-area-textarea'
          disabled={disabled}
          id={id}
          maxLength={maxLength}
          onBlur={onBlur}
          onChange={(e) => {
            onChange(e.target.value);
          }}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          value={value}
        />
      </div>
    );
  },
);

FloatTextArea.propTypes = {
  placeholder: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  className: PropTypes.string,
  maxLength: PropTypes.number,
  hasError: PropTypes.bool,
  disabled: PropTypes.bool,
  onKeyDown: PropTypes.func,
  id: PropTypes.string,
};
